import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ReactMultiSelect, { flattenOptions } from 'dashboard/components/ReactMultiSelect';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import TruncatedText from 'dashboard/components/TruncatedText';
import Button from 'design-system/components/Button';
import Popover from 'design-system/components/Popover';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import inputMixin, { InputMixinProps } from 'design-system/mixins/input';
import useToggle from 'lib/hooks/useToggle';

export interface FilterTagsCondition {
  operator: 'matches-any' | 'does-not-match-any';
  values: string[];
}

const Container = styled('div')`
  position: relative;
`;

const singleSelectStyles = {
  control: () => ({
    height: '34px',
  }),
  container: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  valueContainer: () => ({
    height: `${Spacing[6]}`,
  }),
  indicatorsContainer: () => ({
    height: `${Spacing[6]}`,
  }),
};

const Placeholder = styled('div')<InputMixinProps>`
  ${inputMixin};

  min-width: 200px;

  /* Fixes weird issue with shadow appearing inside placeholder text */
  user-select: none;

  cursor: pointer;
  &:disabled {
    cursor: default;
  }
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledIconCaretDown = styled(CaretDownIcon)`
  margin-left: ${Spacing[2]};

  flex: none;
  display: inline-block;
  pointer-events: none;

  rect[fill],
  path[fill] {
    fill: ${({ theme }) => theme.color.primary.black};
  }
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate50};
  height: ${Spacing[6]};
`;

const Well = styled('div')`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[3]};
`;

const StyledAndButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  width: ${Spacing[8]};
  height: ${Spacing[6]};
`;

const DEFAULT_EMPTY_ROW: FilterTagsCondition = {
  operator: 'matches-any',
  values: [],
};

const getValidFilters = (filters: FilterTagsCondition[][]) => {
  return filters.reduce((groups, group) => {
    const validRows = group.filter((row) => !isEmpty(row.operator) && !isEmpty(row.values));
    if (validRows.length > 0) {
      groups.push(validRows);
    }
    return groups;
  }, [] as FilterTagsCondition[][]);
};

// Custom placeholder generator for Tags multi-select
const placeholderGenerator = (options: OptionType[], value: string[]) => {
  const flatOptions = flattenOptions(options);
  let placeholderText: string;
  if (value.length === 0 || value.length === flatOptions.length) {
    // Render empty placeholder to suggest the filter condition definition is incomplete
    placeholderText = '';
  } else if (value.length === 1) {
    placeholderText =
      (flatOptions.find((option) => option.value === value[0])?.label as string) || '1 Tag';
  } else {
    placeholderText = `${value.length} Tags`;
  }

  return <TruncatedText>{placeholderText}</TruncatedText>;
};

export interface TagsConditionFilterProps {
  ariaLabelledBy?: string;
  values?: FilterTagsCondition[][];
  tagOptions: { value: string; label: string }[];
  productName: string;
  onChange: (filters: FilterTagsCondition[][]) => void;
  className?: string;
}

const TagsConditionFilter: FC<TagsConditionFilterProps> = ({
  ariaLabelledBy,
  values,
  tagOptions,
  productName,
  onChange,
  className,
}) => {
  const [showFilterDropdown, { disable: hideFilterDropdown, set: setFilterDropdown }] =
    useToggle(false);
  const [filterGroups, setFilterGroups] = useState<FilterTagsCondition[][]>(
    !isEmpty(values) ? (values as FilterTagsCondition[][]) : [[DEFAULT_EMPTY_ROW]],
  );

  useEffect(() => {
    setFilterGroups(!isEmpty(values) ? (values as FilterTagsCondition[][]) : [[DEFAULT_EMPTY_ROW]]);
  }, [values]);

  const updateFilterGroups = (filterGroups: FilterTagsCondition[][]) => {
    setFilterGroups(filterGroups);
    const validFilters = getValidFilters(filterGroups);
    const areUserFiltersValid =
      isEqual(validFilters, filterGroups) || isEqual(filterGroups, [[DEFAULT_EMPTY_ROW]]);
    if (areUserFiltersValid) {
      onChange(validFilters);
    }
  };

  const handleFilterDropdownChange = (open: boolean) => {
    // Reset filter groups to last-known valid filters
    if (!isEmpty(values)) {
      setFilterGroups(values as FilterTagsCondition[][]);
    }
    setFilterDropdown(open);
  };

  const handleAddRow = (groupIndex: number) => () => {
    const groups = [...filterGroups];
    groups[groupIndex] = [...filterGroups[groupIndex]];
    groups[groupIndex].push(DEFAULT_EMPTY_ROW);
    // Adding a new row doesn't result in new valid filters
    setFilterGroups(groups);
  };

  const handleRemoveRow = (groupIndex: number, rowIndex: number) => () => {
    const groups = [...filterGroups];
    if (groups[groupIndex].length === 1) {
      groups.splice(groupIndex, 1);
      if (groups.length === 0) {
        groups.push([DEFAULT_EMPTY_ROW]);
        hideFilterDropdown();
      }
    } else {
      groups[groupIndex] = [...filterGroups[groupIndex]];
      groups[groupIndex].splice(rowIndex, 1);
    }
    updateFilterGroups(groups);
  };

  const updateRowValues =
    (groupIndex: number, rowIndex: number) => (row: Partial<FilterTagsCondition>) => {
      const groups = cloneDeep(filterGroups);
      groups[groupIndex][rowIndex] = {
        ...groups[groupIndex][rowIndex],
        ...row,
      };
      updateFilterGroups(groups);
    };

  const getSummaryLabelText = useCallback(() => {
    const numFilters = isEmpty(values)
      ? 0
      : (values as FilterTagsCondition[][]).reduce((acc, group) => (acc += group.length), 0);
    if (numFilters === 0) {
      return 'Add Filter';
    } else if (numFilters === 1) {
      return '1 Filter';
    } else {
      return `${numFilters} Filters`;
    }
  }, [values]);

  const dropdown = (
    <>
      {filterGroups.map((rows: FilterTagsCondition[], groupIndex: number) => (
        <Fragment key={`${groupIndex}`}>
          <Well data-test={`tags-condition-filter__group-${groupIndex}`}>
            {rows.map((row: FilterTagsCondition, rowIndex: number) => (
              <Fragment key={`${groupIndex}-${rowIndex}`}>
                <TagsConditionFilterRow
                  id={`${groupIndex}-${rowIndex}`}
                  label={
                    rowIndex === 0
                      ? groupIndex > 0
                        ? `Or ${productName} tags`
                        : `Where ${productName} tags`
                      : `And ${productName} tags`
                  }
                  row={row}
                  tagOptions={tagOptions}
                  handleRowUpdated={updateRowValues(groupIndex, rowIndex)}
                  remove={handleRemoveRow(groupIndex, rowIndex)}
                />
                <Spacer height={0.5} />
              </Fragment>
            ))}
            <StyledAndButton
              data-test={`tags-condition-filter__and-button-${groupIndex}`}
              id={`tags-condition-filter__and-button-${groupIndex}`}
              variant='basic'
              size='sm'
              width='auto'
              onClick={handleAddRow(groupIndex)}
            >
              And
            </StyledAndButton>
          </Well>
          <Spacer height={2} />
        </Fragment>
      ))}
      <StyledButton
        data-test='tags-condition-filter__or-button'
        id='tags-condition-filter__or-button'
        variant='basic'
        size='sm'
        width='auto'
        onClick={() => setFilterGroups([...filterGroups, [DEFAULT_EMPTY_ROW]])}
      >
        Or group
      </StyledButton>
    </>
  );

  return (
    <Container className={className}>
      <Popover
        trigger={
          <Placeholder
            role='button'
            aria-labelledby={ariaLabelledBy}
            data-test='tags-condition-filter__placeholder'
            id='tags-condition-filter__placeholder'
            active={showFilterDropdown}
            $size='md'
          >
            <TruncatedText>{getSummaryLabelText()}</TruncatedText>
            <StyledIconCaretDown />
          </Placeholder>
        }
        open={showFilterDropdown}
        onOpenChange={handleFilterDropdownChange}
        contentProps={{
          side: 'bottom',
          align: 'start',
          collisionBoundary: [document.getElementById('main')],
        }}
      >
        {dropdown}
      </Popover>
    </Container>
  );
};

const Row = styled('div')`
  width: 100%;
  display: inline-grid;
  align-items: center;
  grid-gap: ${Spacing[3]};
  grid-template-columns: 145px 200px minmax(180px, 1fr) 45px;
  grid-auto-flow: column;
`;

const StyledLabel = styled(Label)`
  min-width: 45px;
`;

const TagValueMultiSelect = styled(ReactMultiSelect)`
  grid-column: span 2;
`;

const RemoveRow = styled('div')`
  padding: ${Spacing[4]};
  cursor: pointer;
`;

const OPERATOR_OPTIONS: OptionType<FilterTagsCondition['operator']>[] = [
  {
    label: 'matches any',
    value: 'matches-any',
  },
  {
    label: 'does not match any',
    value: 'does-not-match-any',
  },
];

interface TagsConditionFilterRowProps {
  id: string;
  label: string;
  row: FilterTagsCondition;
  tagOptions: { label: string; value: string }[];
  remove: () => void;
  handleRowUpdated: (row: Partial<FilterTagsCondition>) => void;
}

const TagsConditionFilterRow: FC<TagsConditionFilterRowProps> = ({
  id,
  label,
  row,
  tagOptions,
  remove,
  handleRowUpdated,
}) => {
  const [filterRow, setFilterRow] = useState<FilterTagsCondition>(row);

  const onChange = (newFields: Partial<FilterTagsCondition>) => {
    const updated = {
      ...filterRow,
      ...newFields,
    };
    setFilterRow(updated);
    handleRowUpdated(updated);
  };

  return (
    <Row data-test={`tags-condition-filter-row__${id}`}>
      <StyledLabel size='sm'>{label}</StyledLabel>
      <ReactSelect
        data-test='tags-condition-filter-row__operator'
        id={`tags-condition-filter-row__operator-${id}`}
        options={OPERATOR_OPTIONS}
        isSearchable={true}
        placeholder='Select...'
        value={filterRow.operator}
        portal={true}
        onChange={(operator) => onChange({ operator })}
        styles={singleSelectStyles}
      />
      <TagValueMultiSelect
        data-test={`tags-condition-filter-row__input-value-${id}`}
        value={filterRow.values}
        options={tagOptions}
        sort
        onChange={(values: string[]) => onChange({ values })}
        placeholderGenerator={placeholderGenerator}
        hasError={isEmpty(filterRow.values)}
        selectAllEnabled
        searchEnabled
        portal
        size='md'
      />
      <RemoveRow
        id={`tags-condition-filter-row__remove-${id}`}
        role='button'
        aria-label='Remove filter'
        onClick={remove}
      >
        <XIcon />
      </RemoveRow>
    </Row>
  );
};

export const SegmentedTagsConditionFilter = styled(TagsConditionFilter)`
  ${Placeholder} {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -1px;
  }
`;

export default TagsConditionFilter;
